import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, forkJoin, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import * as MemberActions from './member.actions';
import { selectMembers, selectMembersQuery } from './member.selectors';
import { MemberService } from '../services/member.service';
import { MemberRomTargetService } from '@member/services/member-rom-target.service';
import { pendingRomTargets } from '@member/utils/triage-rom-target.utils';
import { CreateRomTargetDto, MemberDto, RomTargetDto } from '@shared/dto';
import { JwtService } from '@shared/services/jwt.service';
import { listDeletedParams } from '@shared/utils/pagination.utils';

@Injectable()
export class MemberEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly jwtService = inject(JwtService);
  private readonly userMemberService = inject(MemberService);
  private readonly memberRomTargetService = inject(MemberRomTargetService);

  readMembers$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.readMembers),
    exhaustMap(({ sub, deleted }) => this.userMemberService.read(
      { practitionerId: sub },
      { params: listDeletedParams(deleted) },
    ).pipe(
      map((data) => MemberActions.readMembersSuccess({ data })),
      catchError((error) => of(MemberActions.readMembersFailure({ error })))
    ))
  ));

  // Detail: resolve from the already-loaded list first (no HTTP), otherwise fetch
  // the single entity. The store is the source of truth that both the detail page
  // and the breadcrumb read from.
  readMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.readMember),
    withLatestFrom(this.store.select(selectMembers)),
    switchMap(([{ memberId }, list]) => {
      const cached = list?.find((member) => member.id === memberId);
      if (cached) {
        return of(MemberActions.readMemberSuccess({ member: cached }));
      }

      return this.userMemberService.readOne({ memberId }).pipe(
        map((member) => MemberActions.readMemberSuccess({ member })),
        catchError((error) => of(MemberActions.readMemberFailure({ error })))
      );
    })
  ));

  createMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.createMember),
    exhaustMap(({ sub, dto }) => this.userMemberService.create(dto, { practitionerId: sub }).pipe(
      map((member) => MemberActions.createMemberSuccess({ member })),
      catchError((error) => of(MemberActions.createMemberFailure({ error })))
    ))
  ));

  createTriageMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.createTriageMember),
    exhaustMap((action) => {
      const romTargets = action.romTargets ?? [];
      const member$ = action.mode === 'retry'
        ? of({ id: action.memberId } as MemberDto)
        : this.userMemberService.triage(action.dto, { practitionerId: action.sub });

      return member$.pipe(
        switchMap((member) =>
          this.persistTriageRomTargets(member, romTargets, action.mode === 'retry'),
        ),
        catchError((error) => of(MemberActions.createTriageMemberFailure({ error }))),
      );
    }),
  ));

  // mergeMap, not exhaustMap: route reuse keeps this page alive across `:id`.
  // A save for member B dispatched while A's update is in flight must still hit
  // the API. The failure carries memberId so the open profile can ignore A's 400.
  updateMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.updateMember),
    mergeMap(({ sub, memberId, dto }) => this.userMemberService.update({ practitionerId: sub, memberId }, dto).pipe(
      map((member) => MemberActions.updateMemberSuccess({ member })),
      catchError((error) => of(MemberActions.updateMemberFailure({ error, memberId })))
    ))
  ));

  deleteMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.deleteMember),
    exhaustMap(({ sub, memberId }) => this.userMemberService.delete({ practitionerId: sub, memberId }).pipe(
      map((member) => MemberActions.deleteMemberSuccess({ member })),
      catchError((error) => of(MemberActions.deleteMemberFailure({ error, memberId })))
    ))
  ));

  restoreMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.restoreMember),
    exhaustMap(({ sub, memberId }) => this.userMemberService.restore({ practitionerId: sub, memberId }).pipe(
      map((member) => MemberActions.restoreMemberSuccess({ member })),
      catchError((error) => of(MemberActions.restoreMemberFailure({ error, memberId })))
    ))
  ));

  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      MemberActions.createMemberSuccess,
      MemberActions.createTriageMemberSuccess,
      MemberActions.updateMemberSuccess,
      MemberActions.deleteMemberSuccess,
      MemberActions.restoreMemberSuccess,
    ),
    withLatestFrom(this.store.select(selectMembersQuery)),
    // `sub` scopes the list to the practitioner and comes from the decoded token,
    // which is null until the token lands. `Number(null)` is 0, so dispatching then
    // would refetch the list for practitioner 0 and empty it without a trace.
    filter(() => this.jwtService.sub != null),
    map(([, query]) => MemberActions.readMembers({
      sub: this.jwtService.sub as number,
      deleted: query?.deleted,
    })),
  ));

  /**
   * KH-1434 has no nested triage field. On retry the member already exists, so
   * GET /rom-targets is the record of which writes landed — a previous Save may
   * have persisted some rows before a sibling 400. A changed goal on that row
   * is a PATCH, not another POST. First Save skips the GET: the collection is empty.
   *
   * The GET shares this catchError. If it failed into the effect's outer
   * catchError, the failure action would omit `member` and Save would triage again.
   */
  private persistTriageRomTargets(
    member: MemberDto,
    romTargets: CreateRomTargetDto[],
    alreadyCreated: boolean,
  ) {
    // An empty list on the FIRST save really is a no-op: the member was just
    // created, so there is nothing on the server yet. On a RETRY it is not —
    // the user may have cleared the editor after an earlier partial success
    // (some rows already persisted, a sibling 400). Skipping the GET here
    // would leave those rows orphaned forever, the exact case the delete-diff
    // below exists for.
    if (!romTargets.length && !alreadyCreated) {
      return of(MemberActions.createTriageMemberSuccess({ member }));
    }
    const existing$ = alreadyCreated
      ? this.memberRomTargetService.read({ memberId: member.id })
      : of<RomTargetDto[]>([]);
    return existing$.pipe(
      switchMap((existing) => {
        const pending = pendingRomTargets(romTargets, existing);
        const writes = [
          ...pending.create.map((body) =>
            this.memberRomTargetService.create(body, { memberId: member.id }),
          ),
          ...pending.update.map(({ id, goalDegrees }) =>
            this.memberRomTargetService.update(
              { memberId: member.id, romTargetId: id },
              { goalDegrees },
            ),
          ),
          // A row the user removed from the editor between a failed Save and this
          // retry: the server still has it, and the diff above has no other way to
          // flag it, so it would otherwise stay orphaned forever.
          ...pending.delete.map((romTargetId) =>
            this.memberRomTargetService.delete({ memberId: member.id, romTargetId }),
          ),
        ];
        if (!writes.length) {
          return of(MemberActions.createTriageMemberSuccess({ member }));
        }
        return forkJoin(writes).pipe(
          map(() => MemberActions.createTriageMemberSuccess({ member })),
        );
      }),
      // Member already exists: a GET or ROM 400 must still carry `member`,
      // or Save would call triage() again and duplicate the member.
      catchError((error) => of(MemberActions.createTriageMemberFailure({ error, member }))),
    );
  }
}
